import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { z } from 'zod'

import { Toast } from '@/common/components/Toast'
import { applicationModel } from '@/common/models/application'
import { api, ApiError } from '@/lib/axios'
import { accountModel } from '../models/account'
import {
  applicationAccountModel,
  ApplicationAccountStatus,
} from '../models/applicationAccount'

export const allApplicationDTO = applicationModel.extend({
  ApplicationAccount: z.array(
    applicationAccountModel.extend({
      Account: accountModel,
    }),
  ),
})

export type AllApplication = z.infer<typeof allApplicationDTO>

async function getAllApplications() {
  const { data } = await api.backend.get<AllApplication[]>(
    '/application/findAll',
    {
      params: {
        relations: {
          where: {
            isActive: true,
          },
          include: {
            ApplicationAccount: {
              include: {
                Account: true,
              },
              where: {
                isActive: true,
                status: ApplicationAccountStatus.ACTIVE,
              },
            },
          },
        },
      },
    },
  )

  return data
}

export const useAllApplications = () => {
  const { t } = useTranslation('sso')

  const { data, isLoading, error } = useQuery<
    AllApplication[],
    AxiosError<ApiError>
  >({
    queryKey: ['/application/findAll'],
    queryFn: getAllApplications,
  })

  useEffect(() => {
    if (!error) return

    toast(Toast, {
      data: {
        type: 'danger',
        icon: ServerCrash,
        title: t('applications.paginate_table.toast_error.title'),
        description:
          error?.response?.data?.friendlyMessage ||
          t('applications.paginate_table.toast_error.description'),
      },
    })
  }, [error, t])

  return {
    applications: data || [],
    isLoadingApplications: isLoading,
  }
}
